import React, { useState } from 'react';
import { useCurrentUser } from 'lib/contexts/ApplicationState';
import { Refresh, TrendingUp } from '@mui/icons-material';
import {
  abbreviateNum,
  formatAccountName,
  userIsProOrAdmin,
} from 'lib/helpers';
import { useUpgradeModal } from 'components/UpgradeModal/UpgradeModal';
import { Post, PostScore } from 'lib/types';
import { ProIcon } from 'components/ProBadge/ProIcon';
import { DateTime } from 'luxon';
import { MobileTooltip } from 'components/MobileTooltip';
import { compact, capitalize } from 'lodash';
import { NETWORKS, POST_TYPE_TEXT } from 'lib/constants';
import Link from 'next/link';
import { webAssetUrl } from 'lib/utils/imageUtils';
import {
  ABOVE_AVERAGE_SCORE, BELOW_AVERAGE_SCORE, getAllowedMetrics,
  getGondolaScoreIcon, linkPostToExploreSearch,
} from './helpers';
import { MetricProps } from './types';
import { SparklinesModal } from './SparklinesModal';

interface Props {
  post: Post;
  decimals: number;
  size?: string;
  isScraping?: boolean;
  isPostCard?: boolean;
  onScrapePost?: () => void;
  postScore?: PostScore;
  showSparklines?: boolean;
}

export const PostMetrics = ({
  post,
  decimals,
  size,
  isScraping = false,
  isPostCard = false,
  onScrapePost,
  postScore,
  showSparklines = false,
}: Props) => {
  const [isSparklinesModalOpen, setIsSparklinesModalOpen] = useState(false);
  const { showUpgradeModal } = useUpgradeModal();
  const currentUser = useCurrentUser();

  const renderRefreshStats = () => {
    if (!post.isLive || !onScrapePost) {
      return null;
    }

    const timeAgoBase = DateTime
      .fromISO(post.statsUpdatedAt || post.createdAt)
      .toRelative();

    const timeAgo = timeAgoBase?.match(/seconds/) ? 'just now' : timeAgoBase;

    return (
      <div key={`post-${post.id}-refresh-stats`}>
        <MobileTooltip
          title={(
            <p className="flex items-center">
              <ProIcon width={20} height={24} />
              <span className="ml-2">
                Refresh post stats now (updated
                {' '}
                {timeAgo}
                )
              </span>
            </p>
          )}
          placement="top"
        >

          <button type="button" className="w-full text-center text-orange" onClick={onScrapePost}>
            <Refresh />
          </button>
        </MobileTooltip>
      </div>
    );
  };

  const renderGondolaScore = () => {
    if (!postScore) {
      return null;
    }
    /**
     * We don't show the gondola score for posts that are less than 3 days old.
     * This is because the score is not accurate for the first few days of a post.
     */
    const THREE_DAYS_AGO = DateTime.now().minus({ days: 3 }).toISO();

    if (post.postedAt > THREE_DAYS_AGO) {
      return (
        <div key={`post-${post.id}-gondola-score`}>
          <MobileTooltip
            placement="top"
            arrow
            title="The Gondola Score for this post is not available yet. Gondola Score is calculated based on the average engagement of similar posts from the same account and network. We wait thee days from the post date to calculate the score."
          >
            <p className="font-light">
              <img src={webAssetUrl('Logo_Black.png')} alt="Gondola Score" className="w-5" />
            </p>
          </MobileTooltip>
        </div>
      );
    }

    const canViewScore = userIsProOrAdmin(currentUser);

    const score = postScore.engagements / postScore.averageEngagements;

    if (!canViewScore) {
      return (
        <div key={`post-${post.id}-gondola-score`}>
          <MobileTooltip
            placement="top"
            arrow
            title={(
              <span>
                Gondola Score is calculated based on the average engagement
                of similar posts from the same account and network.
                {' '}
                <Link href="/plans" className="underline">
                  To view the Gondola Score for this post, upgrade to PRO.
                </Link>
              </span>
            )}
          >
            <p className="font-light flex">
              <img src={webAssetUrl('Logo_Black.png')} alt="Gondola Score" className="w-6" />
              {getGondolaScoreIcon(score)}
              <span className="blur-sm">0.0</span>
              x
            </p>
          </MobileTooltip>
        </div>
      );
    }

    let trendText = 'about the same as';
    if (score && score > ABOVE_AVERAGE_SCORE) {
      trendText = 'greater than';
    } else if (score && score < BELOW_AVERAGE_SCORE) {
      trendText = 'less than';
    }

    return (
      <div key={`post-${post.id}-gondola-score`}>
        <MobileTooltip
          placement="top"
          arrow
          title={(
            <span>
              Gondola Score:
              {' '}
              {score.toFixed(1)}
              x.
              {' '}
              This post&apos;s engagement is
              {' '}
              {abbreviateNum(postScore.engagements, 1)}
              . That is
              {' '}
              {trendText}
              {' '}
              the average of
              {' '}
              {abbreviateNum(postScore.averageEngagements, 1)}
              {' '}
              for
              {' '}
              <Link href={linkPostToExploreSearch(post)} className="underline">
                {NETWORKS[post.network].name}
                {' '}
                {POST_TYPE_TEXT[postScore.type].toLowerCase()}
                {' '}
                posts from @
                {post.account}
                {' '}
                during the same time period.
              </Link>
              {' '}
              This is a BETA feature and subject to change.
            </span>
          )}
        >
          <p className="font-light flex">
            <img src={webAssetUrl('Logo_Black.png')} alt="Gondola Score" className="w-6" />
            {getGondolaScoreIcon(score)}
            {score.toFixed(1)}
            x
          </p>
        </MobileTooltip>
      </div>
    );
  };

  const clickSparklinesButton = () => {
    if (userIsProOrAdmin(currentUser)) {
      setIsSparklinesModalOpen(true);
    } else {
      showUpgradeModal(`View this @${formatAccountName(post.account, post.network, post?.networkAccount?.displayName)} post's historical performance`);
    }
  };

  const renderSparklinesButton = () => {
    const MINIMUM_DAYS_FOR_SPARKLINES = 3;
    if (!showSparklines || (post.dailyStats?.length || 0) < MINIMUM_DAYS_FOR_SPARKLINES) {
      return null;
    }

    return (
      <div key={`post-${post.id}-sparklines`}>
        <MobileTooltip
          title="View post performance over time"
          placement="top"
        >
          <button
            type="button"
            className="w-full text-center"
            onClick={clickSparklinesButton}
          >
            <TrendingUp />
          </button>
        </MobileTooltip>
      </div>
    );
  };

  const renderMetrics = () => {
    const allowedMetrics = getAllowedMetrics(post.network, isPostCard);
    const metricData = allowedMetrics.map((metric: MetricProps) => {
      const metricValue = post[metric.field] as number;
      if (!metricValue && !metric.renderIfNull) {
        return null;
      }
      return (
        <div
          key={`post-${post.id}-${metric.field}`}
        >
          <MobileTooltip title={capitalize(metric.label)}>
            <p className="font-light">
              {metric.icon}
            &nbsp;
              {abbreviateNum(metricValue, decimals)}
            </p>
          </MobileTooltip>
        </div>
      );
    });
    const firstRow = compact(metricData);
    const secondRow = compact([
      renderGondolaScore(),
      renderSparklinesButton(),
      renderRefreshStats(),
    ]);

    const hasSecondRow = secondRow.length > 0;
    return (
      <>
        {/* Mobile layout - stacked rows */}
        <div className="flex flex-col gap-2 lg:hidden w-full">
          <div className={`flex ${hasSecondRow ? 'justify-center' : 'justify-around'} items-center gap-12`}>
            {firstRow}
          </div>
          {hasSecondRow && (
            <div className="flex justify-center items-center gap-12">
              {secondRow}
            </div>
          )}
        </div>
        {/* Desktop layout - single row */}
        <div className="hidden lg:flex justify-evenly items-center w-full">
          {firstRow}
          {secondRow}
        </div>
      </>
    );
  };

  return (
    <>
      <div className={`flex gap-1 flex-wrap justify-between items-center text-center p-2 ${size === 'small' && 'text-sm'} ${isScraping ? 'animate-pulse' : ''}`}>
        {renderMetrics()}
      </div>
      {showSparklines && post.dailyStats && (
        <SparklinesModal
          open={isSparklinesModalOpen}
          onClose={() => setIsSparklinesModalOpen(false)}
          dailyStats={post.dailyStats}
          post={post}
        />
      )}
    </>
  );
};
